/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `VideoLibrary` for the Caintra Connect frontend.
 * @module components/VideoTutorialsSection/VideoLibrary
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./VideoLibrary.module.css";
import { use, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { AppContext } from "../../context/AppContext";
import { canEditHomepage } from "../../utils/accessControl";
import { ReactComponent as MoreIcon } from "../../assets/icons/Chat/MoreIcon.svg";
import {
  getVideoSections,
  deleteVideoSection,
} from "../../logic/videoTutorialSection/index.js";
import {
  getVideoTutorials,
  deleteVideoTutorial,
} from "../../logic/videoTutorial/index.js";
import { getUser } from "../../logic/user/index.js";
import VideoTutorialSectionModal from "../../modals/VideoTutorialSectionModal/index.jsx";
import VideoPlayerModal from "../../modals/VideoPlayerModal/index.jsx";
import ConfirmationModal from "../../modals/ConfirmationModal/index.jsx";
import VideoThumbnail from "./VideoThumbnail.jsx";

import { ReactComponent as TooltipIcon } from "../../assets/icons/VideoLibrary/TooltipIcon.svg";
import { ReactComponent as PlayIcon } from "../../assets/icons/VideoLibrary/PlayIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/PartnersImage/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/PartnersImage/DeleteIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/PartnersImage/AddIcon.svg";
import VideoTutorialModal from "../../modals/VideoTutorialModal/index.jsx";

const DROPDOWN_ITEM_CLASS = styles.dropdownItem;
const DROPDOWN_ITEM_DISABLED_CLASS = styles.dropdownItemDisabled;

const VideoLibrary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permissions } = use(AppContext);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [allVideos, setAllVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [selectedSectionForEdit, setSelectedSectionForEdit] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [showVideoPlayerModal, setShowVideoPlayerModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const confirmationDialogRef = useRef(null);
  const [showVideoTutorialModal, setShowVideoTutorialModal] = useState(false);
  const [selectedVideoForEdit, setSelectedVideoForEdit] = useState(null);
  const [videoModalMode, setVideoModalMode] = useState("create");
  const [showDeleteVideoConfirmation, setShowDeleteVideoConfirmation] =
    useState(false);
  const [selectedVideoForDelete, setSelectedVideoForDelete] = useState(null);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);
  const deleteVideoConfirmationDialogRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      return;
    }

    const userData = await getUser(userId, navigate);
    if (userData) {
      setUser(userData);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sectionsData, videosData] = await Promise.all([
        getVideoSections(navigate),
        getVideoTutorials(navigate),
      ]);
      // Sort sections by createdAt (oldest first, so they appear on the left)
      const sortedSections = [...sectionsData].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateA - dateB;
      });
      setSections(sortedSections);
      setAllVideos(videosData);
      if (sortedSections.length > 0) {
        setSelectedSection(sortedSections[0]);
      }
    } catch (error) {
      logger.error("Error fetching data:", { err: sanitizeError(error) });
    } finally {
      setLoading(false);
    }
  };

  // Get videos for the selected section
  const getFilteredVideos = () => {
    if (!selectedSection || allVideos.length === 0) {
      return [];
    }

    const sectionId = selectedSection._id || selectedSection.id;
    if (!sectionId) {
      return [];
    }

    // Filter videos by section ID and sort by createdAt (oldest first, same as sections)
    return allVideos
      .filter(
        (video) =>
          video.sectionId === sectionId ||
          video.sectionId === selectedSection._id ||
          video.sectionId === selectedSection.id,
      )
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateA - dateB;
      });
  };

  const videos = getFilteredVideos();

  const handleVideoClick = (video) => {
    if (video && video.videoUrl) {
      setSelectedVideo(video);
      setShowVideoPlayerModal(true);
    }
  };

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
  };

  const handleVideoPlayerModalClose = () => {
    setShowVideoPlayerModal(false);
    setSelectedVideo(null);
  };

  const handleSectionClick = (section) => {
    setSelectedSection(section);
  };

  const toggleDropdown = (event) => {
    event.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleEditSection = () => {
    if (sections.length === 0) {
      return;
    }
    setSelectedSectionForEdit(selectedSection);
    setModalMode("edit");
    setShowSectionModal(true);
    setShowDropdown(false);
  };

  const handleAddSection = () => {
    setSelectedSectionForEdit(null);
    setModalMode("create");
    setShowSectionModal(true);
    setShowDropdown(false);
  };

  const handleSectionModalClose = () => {
    setShowSectionModal(false);
    setSelectedSectionForEdit(null);
  };

  const handleSectionCreated = () => {
    fetchData();
  };

  const handleDeleteSection = () => {
    if (selectedSection) {
      setShowDeleteConfirmation(true);
      setShowDropdown(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedSection) {
      return;
    }

    setIsDeleting(true);
    const sectionId = selectedSection._id || selectedSection.id;

    try {
      const success = await deleteVideoSection(sectionId, navigate);
      if (success) {
        // Reset selected section if it was deleted
        setSelectedSection(null);
        // Refresh data
        await fetchData();
      } else {
        logger.error("Failed to delete section");
        // You might want to show an error message here
      }
    } catch (error) {
      logger.error("Error deleting section:", { err: sanitizeError(error) });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleAddVideo = () => {
    if (!selectedSection) {
      return;
    }
    setSelectedVideoForEdit(null);
    setVideoModalMode("create");
    setShowVideoTutorialModal(true);
  };

  const handleEditVideo = (video) => {
    setSelectedVideoForEdit(video);
    setVideoModalMode("edit");
    setShowVideoTutorialModal(true);
  };

  const handleDeleteVideo = (video) => {
    setSelectedVideoForDelete(video);
    setShowDeleteVideoConfirmation(true);
  };

  const handleConfirmDeleteVideo = async () => {
    if (!selectedVideoForDelete) {
      return;
    }

    setIsDeletingVideo(true);
    const videoId = selectedVideoForDelete._id || selectedVideoForDelete.id;

    try {
      const success = await deleteVideoTutorial(videoId, navigate);
      if (success) {
        // Refresh data
        await fetchData();
      } else {
        logger.error("Failed to delete video");
      }
    } catch (error) {
      logger.error("Error deleting video:", { err: sanitizeError(error) });
    } finally {
      setIsDeletingVideo(false);
      setShowDeleteVideoConfirmation(false);
      setSelectedVideoForDelete(null);
    }
  };

  const handleVideoTutorialModalClose = () => {
    setShowVideoTutorialModal(false);
    setSelectedVideoForEdit(null);
  };

  const handleVideoCreated = () => {
    fetchData();
  };

  const handleVideoUpdated = () => {
    fetchData();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showDropdown) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showDropdown]);

  const canManageVideos = canEditHomepage(user, permissions);

  const renderAddVideoButton = () => {
    if (!canManageVideos || !selectedSection) {
      return null;
    }

    return (
      <button className={styles.addVideoButton} onClick={handleAddVideo}>
        <AddIcon />
        {t("components.videoLibrary.addVideo")}
      </button>
    );
  };

  const renderDropdown = () => {
    if (!canManageVideos) {
      return null;
    }

    return (
      <div className={styles.dropdownContainer}>
        <button className={styles.dropdownButton} onClick={toggleDropdown}>
          <MoreIcon />
        </button>
        {showDropdown && (
          <div className={styles.dropdownMenu}>
            <button className={DROPDOWN_ITEM_CLASS} onClick={handleAddSection}>
              {t("components.videoLibrary.addSection")}
            </button>
            <button
              className={`${DROPDOWN_ITEM_CLASS} ${
                sections.length === 0 ? DROPDOWN_ITEM_DISABLED_CLASS : ""
              }`}
              onClick={handleEditSection}
              disabled={sections.length === 0}
            >
              {t("components.videoLibrary.editSection")}
            </button>
            <div className={styles.dropdownDivider}></div>
            <button
              className={`${DROPDOWN_ITEM_CLASS} ${styles.deleteItem} ${
                !selectedSection ? DROPDOWN_ITEM_DISABLED_CLASS : ""
              }`}
              onClick={handleDeleteSection}
              disabled={!selectedSection}
            >
              {t("components.videoLibrary.deleteSection")}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <div className={styles.videoTutorialsContainer}>
          <div className={styles.videoTutorialsHeader}>
            <h2 className={styles.videoTutorialsTitle}>
              {t("components.videoLibrary.title")}
            </h2>
            <div className={styles.border}></div>
            <div className={styles.headerActions}>{renderAddVideoButton()}</div>
            {renderDropdown()}
          </div>
          <div className={styles.loading}>
            {t("components.videoLibrary.loading")}
          </div>
        </div>
      ) : sections.length === 0 ? (
        <div className={styles.videoTutorialsContainer}>
          <div className={styles.videoTutorialsHeader}>
            <h2 className={styles.videoTutorialsTitle}>
              {t("components.videoLibrary.title")}
            </h2>
            <div className={styles.border}></div>
            <div className={styles.headerActions}>{renderAddVideoButton()}</div>
            {renderDropdown()}
          </div>
          <div className={styles.noContent}>
            {t("components.videoLibrary.noSectionsOrVideos")}
          </div>
        </div>
      ) : (
        <div className={styles.videoTutorialsContainer}>
          <div className={styles.videoTutorialsHeader}>
            <h2 className={styles.videoTutorialsTitle}>
              {t("components.videoLibrary.title")}
            </h2>
            <div className={styles.border}></div>
            <div className={styles.headerActions}>{renderAddVideoButton()}</div>
            {renderDropdown()}
          </div>

          <div className={styles.sectionsContainer}>
            {sections.map((section) => {
              const isSelected =
                selectedSection && selectedSection._id === section._id;
              return (
                <button
                  key={section._id}
                  className={`${styles.sectionOval} ${
                    isSelected ? styles.activeSection : ""
                  }`}
                  onClick={() => handleSectionClick(section)}
                >
                  {section.name}
                </button>
              );
            })}
          </div>

          {selectedSection && (
            <div className={styles.videosContainer}>
              {videos.length === 0 ? (
                <div className={styles.noContent}>
                  {t("components.videoLibrary.noVideosInSection")}
                </div>
              ) : (
                <div className={styles.videosGrid}>
                  {videos.map((video, index) => (
                    <div
                      key={video._id || video.id || index}
                      className={styles.videoCard}
                    >
                      <div className={styles.videoThumbnail}>
                        {video.videoUrl ? (
                          <VideoThumbnail
                            videoUrl={video.videoUrl}
                            thumbnailSecond={video.thumbnailSecond || 0}
                          />
                        ) : (
                          <div className={styles.thumbnailPlaceholder}>
                            <svg
                              width="48"
                              height="48"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 5V19L19 12L8 5Z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                        )}
                        {canManageVideos && (
                          <div className={styles.videoActions}>
                            <button
                              className={styles.editVideoButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditVideo(video);
                              }}
                              title={t("components.videoLibrary.editVideo")}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className={styles.deleteVideoButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteVideo(video);
                              }}
                              title={t("components.videoLibrary.deleteVideo")}
                            >
                              <DeleteIcon />
                            </button>
                          </div>
                        )}
                        <button
                          className={styles.playButton}
                          onClick={() => handleVideoClick(video)}
                        >
                          <PlayIcon />
                          <span>{t("components.videoLibrary.playButton")}</span>
                        </button>
                        {(video.name || video.description) && (
                          <div
                            className={styles.tooltipContainer}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className={styles.tooltipIcon}>
                              <TooltipIcon />
                            </div>
                            <div className={styles.tooltip}>
                              {video.name && (
                                <div className={styles.tooltipName}>
                                  {video.name}
                                </div>
                              )}
                              {video.name && video.description && (
                                <div className={styles.tooltipDivider}></div>
                              )}
                              {video.description && (
                                <div className={styles.tooltipDescription}>
                                  {video.description}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <VideoTutorialSectionModal
        open={showSectionModal}
        onClose={handleSectionModalClose}
        section={selectedSectionForEdit}
        mode={modalMode}
        onSectionCreated={handleSectionCreated}
      />

      <VideoPlayerModal
        open={showVideoPlayerModal}
        onClose={handleVideoPlayerModalClose}
        video={selectedVideo}
        section={selectedSection}
        allVideos={allVideos}
        onVideoSelect={handleVideoSelect}
      />

      <VideoTutorialModal
        open={showVideoTutorialModal}
        onClose={handleVideoTutorialModalClose}
        video={selectedVideoForEdit}
        sectionId={selectedSection?._id || selectedSection?.id}
        mode={videoModalMode}
        onVideoCreated={handleVideoCreated}
        onVideoUpdated={handleVideoUpdated}
      />

      <ConfirmationModal
        dialogRef={confirmationDialogRef}
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title={t("components.videoLibrary.deleteSectionTitle")}
        message={
          selectedSection
            ? t("components.videoLibrary.deleteSectionMessage", {
                sectionName: selectedSection.name || "",
              })
            : t("components.videoLibrary.deleteSectionMessage", {
                sectionName: "",
              })
        }
        confirmButtonText={t("components.videoLibrary.confirmDelete")}
        cancelButtonText={t("components.videoLibrary.cancelDelete")}
        isLoading={isDeleting}
        loadingButtonText={t("components.videoLibrary.deleting")}
      />

      <ConfirmationModal
        dialogRef={deleteVideoConfirmationDialogRef}
        open={showDeleteVideoConfirmation}
        onClose={() => {
          setShowDeleteVideoConfirmation(false);
          setSelectedVideoForDelete(null);
        }}
        onConfirm={handleConfirmDeleteVideo}
        title={t("components.videoLibrary.deleteVideoTitle")}
        message={
          selectedVideoForDelete
            ? t("components.videoLibrary.deleteVideoMessage", {
                videoName: selectedVideoForDelete.name || "",
              })
            : t("components.videoLibrary.deleteVideoMessage", {
                videoName: "",
              })
        }
        confirmButtonText={t("components.videoLibrary.confirmDelete")}
        cancelButtonText={t("components.videoLibrary.cancelDelete")}
        isLoading={isDeletingVideo}
        loadingButtonText={t("components.videoLibrary.deleting")}
      />
    </>
  );
};

export default VideoLibrary;
